import { useState, useEffect } from 'react';
import { Spinner } from '@klaro7/ui';
import {
  CONTRACT_GROUPS,
  RegimeFiscale,
  type ContractGroupKey,
  type ContractDocItem,
} from '@klaro7/shared-types';

import { extractErrorMessage } from '@/lib/api';
import { useContractCompliance, useAcceptContracts } from './api';

const FileIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

function docVisibleForRegime(doc: ContractDocItem, regime: RegimeFiscale): boolean {
  if (!doc.regime) return true;
  const isPiva = regime !== RegimeFiscale.OCCASIONALE;
  return doc.regime === (isPiva ? 'piva' : 'occasionale');
}

/**
 * Contractual documents the consultant must read and accept before going online.
 * `regime` decides which contract variant (P.IVA vs provisional) is shown.
 */
export function ContractsSection({ regime }: { regime: RegimeFiscale }) {
  const { data: compliance, isLoading } = useContractCompliance();
  const accept = useAcceptContracts();

  const [checked, setChecked] = useState<Record<ContractGroupKey, boolean>>({
    contratto: false,
    codiceEtico: false,
    regolamento: false,
  });
  const [error, setError] = useState<string | null>(null);

  // Seed checkboxes from already-accepted groups.
  useEffect(() => {
    if (!compliance) return;
    const next = { contratto: false, codiceEtico: false, regolamento: false } as Record<ContractGroupKey, boolean>;
    for (const g of compliance.groups) next[g.key] = g.accepted;
    setChecked(next);
  }, [compliance]);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="flex justify-center py-6">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  const acceptedMap = new Map(compliance?.groups.map((g) => [g.key, g.accepted]) ?? []);

  // Only submit groups newly checked that aren't already accepted at this version.
  const pendingGroups = (Object.keys(checked) as ContractGroupKey[]).filter(
    (k) => checked[k] && !acceptedMap.get(k)
  );

  const handleSubmit = async () => {
    setError(null);
    if (pendingGroups.length === 0) return;
    try {
      await accept.mutateAsync(pendingGroups);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const allAccepted = compliance?.allAccepted ?? false;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold text-neutral-900">Contratti e documenti</h2>
        {allAccepted && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Tutti accettati
          </span>
        )}
      </div>
      <p className="mb-5 text-sm text-neutral-500">
        Leggi e accetta tutti i documenti per poterti mettere online e ricevere chiamate.
      </p>

      <div className="space-y-4">
        {CONTRACT_GROUPS.map((group) => {
          const docs = group.docs.filter((d) => docVisibleForRegime(d, regime));
          const isAccepted = acceptedMap.get(group.key) ?? false;
          return (
            <div key={group.key} className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-sm font-semibold text-neutral-800">{group.title}</p>
              <ul className="mt-2 space-y-1.5">
                {docs.map((doc) => (
                  <li key={doc.slug}>
                    <a
                      href={doc.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:underline"
                    >
                      <FileIcon />
                      {doc.title}
                    </a>
                  </li>
                ))}
              </ul>
              <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={checked[group.key]}
                  disabled={isAccepted}
                  onChange={(e) => setChecked((c) => ({ ...c, [group.key]: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal disabled:opacity-60"
                />
                <span className="text-sm text-neutral-700">{group.consentLabel}</span>
              </label>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!allAccepted && (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={accept.isPending || pendingGroups.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-orange/90 disabled:opacity-60 transition-colors"
          >
            {accept.isPending && <Spinner size="sm" />}
            Accetta e salva
          </button>
        </div>
      )}
    </div>
  );
}
